import React, { useEffect, useRef, useState } from 'react';
import { Archive, ArchiveRestore, MoreHorizontal, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Conversation } from '../../../models/agentConversation';
import { displayTitle, relativeWhen } from './conversationPresentation';
import { PatientBadge } from './PanelChrome';

interface ConversationRowProps {
  conversation: Conversation;
  current: boolean;
  unread: boolean;
  // Hidden inside the open patient's group, where every row is that patient.
  showPatient: boolean;
  onOpen: () => void;
  onRename: (title: string) => Promise<void>;
  onToggleArchive: () => void;
  onDelete: () => void;
}

const ConversationRow: React.FC<ConversationRowProps> = ({
  conversation: c,
  current,
  unread,
  showPatient,
  onOpen,
  onRename,
  onToggleArchive,
  onDelete,
}) => {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Set when "Renombrar" closes the menu, so focus goes to the input rather
  // than back to the menu button (which would blur and end the rename).
  const renameFromMenuRef = useRef(false);
  const title = displayTitle(c);

  useEffect(() => {
    if (renaming && !renameFromMenuRef.current) inputRef.current?.select();
  }, [renaming]);

  const startRename = () => {
    renameFromMenuRef.current = true;
    setDraft(title.pending ? '' : c.title);
    setRenaming(true);
  };

  const commit = async () => {
    const next = draft.trim();
    setRenaming(false);
    if (next && next !== c.title) await onRename(next.slice(0, 120));
  };


  return (
    <div
      className={cn(
        'group relative flex items-start gap-2 py-2 pr-2 pl-3.5 hover:bg-muted',
        current && 'bg-primary-subtle hover:bg-primary-subtle',
      )}
    >
      <span
        className={cn('mt-1.5 size-2 shrink-0 rounded-full', unread ? 'bg-highlight' : 'bg-transparent')}
        aria-hidden="true"
      />
      {renaming ? (
        <input
          ref={inputRef}
          value={draft}
          maxLength={120}
          aria-label="Nuevo título"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setRenaming(false);
          }}
          className="h-8 min-w-0 flex-1 rounded-control border border-ring bg-card px-2 text-sm"
        />
      ) : (
        <button
          type="button"
          onClick={onOpen}
          aria-current={current ? 'true' : undefined}
          className="min-w-0 flex-1 cursor-pointer border-0 bg-transparent p-0 text-left"
        >
          <span className="flex items-baseline gap-2">
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-sm',
                title.pending ? 'font-medium text-muted-foreground italic' : 'font-semibold text-foreground',
              )}
            >
              {title.text}
            </span>
            <span className="shrink-0 font-mono text-xs text-muted-foreground">{relativeWhen(c.lastMessageAt)}</span>
          </span>
          {(showPatient || c.lastMessagePreview) && (
            <span className="mt-1 flex min-w-0 items-center gap-2">
              {showPatient && <PatientBadge name={c.patient?.name ?? null} className="max-w-[55%] shrink-0" />}
              {c.lastMessagePreview && (
                <span className="min-w-0 truncate text-xs text-muted-foreground">{c.lastMessagePreview.text}</span>
              )}
            </span>
          )}
          {unread && <span className="sr-only">Respuesta sin leer.</span>}
          {(c.pendingProposals > 0 || c.isLegacy) && (
            <span className="mt-1 flex flex-wrap gap-1.5">
              {c.pendingProposals > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-subtle px-2 py-0.5 text-xs font-semibold text-primary">
                  <Sparkles className="size-3" aria-hidden="true" />
                  Propuestas pendientes ({c.pendingProposals})
                </span>
              )}
              {c.isLegacy && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  Antes de las conversaciones por paciente
                </span>
              )}
            </span>
          )}
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Opciones de «${title.text}»`}
            className="shrink-0 opacity-60 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48"
          onCloseAutoFocus={(e) => {
            if (!renameFromMenuRef.current) return;
            renameFromMenuRef.current = false;
            e.preventDefault();
            inputRef.current?.focus();
            inputRef.current?.select();
          }}
        >
          <DropdownMenuItem onSelect={startRename}>
            <Pencil /> Renombrar
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onToggleArchive}>
            {c.archived ? <ArchiveRestore /> : <Archive />}
            {c.archived ? 'Desarchivar' : 'Archivar'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onSelect={onDelete}>
            <Trash2 /> Eliminar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ConversationRow;
